import { useEffect, useState } from "react";
import type { ComboboxOption } from "@/components/ui/combobox";

export interface LocationData {
  countries: ComboboxOption[];
  citiesForCountry: (countryName: string) => ComboboxOption[];
}

// The country-state-city dataset is large (~2MB), so we load it lazily via a dynamic
// import — webpack splits it into its own chunk that only downloads when a form that
// needs it actually mounts, keeping it out of the main bundle. The promise is cached
// module-level so the chunk is fetched at most once and shared across all callers.
let cachePromise: Promise<LocationData> | null = null;

export function loadLocationData(): Promise<LocationData> {
  if (!cachePromise) {
    cachePromise = import("country-state-city").then(({ Country, City }) => {
      const countries: ComboboxOption[] = Country.getAllCountries().map((c) => ({ value: c.name, label: c.name }));
      const isoByName = new Map(Country.getAllCountries().map((c) => [c.name, c.isoCode]));
      const citiesForCountry = (countryName: string): ComboboxOption[] => {
        const iso = isoByName.get(countryName);
        if (!iso) return [];
        const seen = new Set<string>();
        const out: ComboboxOption[] = [];
        for (const city of City.getCitiesOfCountry(iso) || []) {
          if (seen.has(city.name)) continue;
          seen.add(city.name);
          out.push({ value: city.name, label: city.name });
        }
        out.sort((a, b) => a.label.localeCompare(b.label));
        return out;
      };
      return { countries, citiesForCountry };
    });
  }
  return cachePromise;
}

// Loads the location dataset on mount and returns it once ready (null while loading).
export function useLocationData(): LocationData | null {
  const [data, setData] = useState<LocationData | null>(null);
  useEffect(() => {
    let alive = true;
    loadLocationData().then((d) => { if (alive) setData(d); });
    return () => { alive = false; };
  }, []);
  return data;
}
